import React from 'react';
import styled from 'styled-components';
import { num, clock } from '../../contexts/FarmStatusContext';
import { GlanceCard } from './ui';
import { trendOf } from './helpers';

// The last 24 h of outside temperature: one quiet line, the latest reading marked, the low
// and high on the right. Fills the card's foot so it ends level with the two beside it.
const Day = styled.div`
  margin-top: auto;
  padding-top: 16px;
  .cap { display: flex; justify-content: space-between; gap: 8px; font-size: 12px; color: ${p => p.theme.colors.textMuted}; }
  .cap b { font-weight: 500; color: ${p => p.theme.colors.textSecondary}; }
  .plot { position: relative; height: 48px; margin-top: 12px; }
  svg { display: block; width: 100%; height: 100%; overflow: visible; }
  .grid { stroke: ${p => p.theme.colors.border}; stroke-width: 1; }
  .ln { fill: none; stroke: ${p => p.theme.colors.textSecondary}; stroke-width: 1.5; stroke-linejoin: round; stroke-linecap: round; }
  .now { position: absolute; width: 8px; height: 8px; margin: -4px 0 0 -4px; border-radius: 50%;
    background: ${p => p.theme.colors.primary}; box-shadow: 0 0 0 2px ${p => p.theme.colors.surface}; }
  .axis { display: flex; justify-content: space-between; margin-top: 4px; font-size: 11px; color: ${p => p.theme.colors.textMuted}; font-variant-numeric: tabular-nums; }
`;

function DayLine({ points, now, trend }) {
  const pts = points.filter(p => p.v != null);
  if (pts.length < 3) return null;
  const t0 = pts[0].t;
  const t1 = pts[pts.length - 1].t;
  const vs = pts.map(p => p.v);
  const lo = Math.min(...vs);
  const hi = Math.max(...vs);
  const pad = Math.max(0.5, (hi - lo) * 0.12);
  const x = t => ((t - t0) / Math.max(1, t1 - t0)) * 100;
  const y = v => (1 - (v - (lo - pad)) / (hi - lo + 2 * pad)) * 100;
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(2)},${y(p.v).toFixed(2)}`).join(' ');
  const last = pts[pts.length - 1];

  return (
    <Day>
      <div className="cap">
        <span>
          {!trend ? 'Last 24 h' : Math.abs(trend.delta) < 0.5 ? <><b>Steady</b> since {clock(trend.since, now)}</>
            : <><b>{trend.delta > 0 ? 'Warming' : 'Cooling'}</b>, {trend.delta > 0 ? '+' : '−'}{Math.abs(trend.delta).toFixed(1)}° since {clock(trend.since, now)}</>}
        </span>
        <span>{lo.toFixed(0)}° to {hi.toFixed(0)}°</span>
      </div>
      <div className="plot">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {[0, 50, 100].map(g => <line key={g} className="grid" x1="0" x2="100" y1={g} y2={g} vectorEffect="non-scaling-stroke" />)}
          <path className="ln" d={d} vectorEffect="non-scaling-stroke" />
        </svg>
        <span className="now" style={{ left: `${x(last.t)}%`, top: `${y(last.v)}%` }} />
      </div>
      {/* Same clock as the caption above; clock(ts, ts) is the time alone, without "yesterday". */}
      <div className="axis"><span>{clock(t0, t0)}</span><span>{clock(Math.round((t0 + t1) / 2), Math.round((t0 + t1) / 2))}</span><span>now</span></div>
    </Day>
  );
}

/* Outside temperature and conditions, how much cooler the container is, and the day's line. */
export default function WeatherCard({ outside, weather24, climate, now }) {
  const points = (weather24 || []).map(w => ({ t: w.timestamp, v: num(w.temp_c) }));
  const outsideTrend = trendOf(points, 3 * 3600);
  const fig = (v, digits, unit) => (outside && num(v) != null ? `${num(v).toFixed(digits)}${unit}` : '--');

  return (
    <GlanceCard to="/rooms" $status={outside ? 'normal' : 'offline'}>
      <div className="k">Weather outside <span className="word"><i />{outside?.condition || (outside ? 'Live' : 'No data')}</span></div>
      <div className="big">{outside ? `${num(outside.temp_c)?.toFixed(1)}°` : '--'}<small>{outside ? `feels ${num(outside.apparent_temp_c)?.toFixed(0) ?? '-'}°` : ''}</small></div>
      <div className="facts">
        <div><b>{fig(outside?.humidity_pct, 0, '%')}</b><span>humidity</span></div>
        <div><b>{outside ? fig(outside.precipitation_mm ?? 0, 1, ' mm') : '--'}</b><span>rain</span></div>
        <div><b>{fig(outside?.wind_speed_kmh, 0, ' km/h')}</b><span>wind</span></div>
      </div>
      <div className="line">
        {outside && climate.temp !== null
          ? <>Container is <b>{Math.abs(outside.temp_c - climate.temp).toFixed(1)}° {climate.temp < outside.temp_c ? 'cooler' : 'warmer'}</b> than outside</>
          : outside ? 'Container sensors not reporting' : 'Weather is not being recorded'}
      </div>
      <DayLine points={points} now={now} trend={outsideTrend} />
    </GlanceCard>
  );
}
